import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { Strophe, $iq, $msg, $pres } from "strophe.js";
import { notifContext } from "./notifContext";
import { useLocation } from "react-router-dom";

export const XmppContext = createContext();

const BOSH_SERVICE = "wss://chat.cuaps.id/ws";

export function XmppProvider({ children }) {
  const location = useLocation();
  const {
    unreadModule,
    setNewMessage,
    setUnreadModule,
    setNewMessageRead,
    addAudioAlert,
  } = useContext(notifContext);
  const connectionRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [jid, setJid] = useState(null);
  const [connected, setConnected] = useState(false);

  // 🆕 store online users
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  // 🆕 store full roster (contact list)
  const [roster, setRoster] = useState([]);

  // 🔌 Connect to ejabberd

  const connect = (userJid, password, isReconnect = false) => {
    return new Promise((resolve, reject) => {
      const conn = new Strophe.Connection(BOSH_SERVICE);
      connectionRef.current = conn;

      conn.connect(userJid, password, (status) => {
        switch (status) {
          case Strophe.Status.CONNECTING:
            break;

          case Strophe.Status.AUTHENTICATING:
            break;

          case Strophe.Status.AUTHFAIL:
            console.error("❌ Authentication failed");
            setConnected(false);
            reject(new Error("XMPP authentication failed"));
            break;

          case Strophe.Status.CONNECTED:
            console.log(
              isReconnect ? "🔄 Reconnected" : "✅ Connected",
              userJid
            );
            setConnected(true);
            setJid(userJid);
            conn.send($pres().tree());
            getListContact(conn);
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
              reconnectTimeoutRef.current = null;
            }
            resolve({ success: true, jid: userJid });
            break;

          case Strophe.Status.DISCONNECTED:
            setConnected(false);
            if (!reconnectTimeoutRef.current) {
              reconnectTimeoutRef.current = setTimeout(() => {
                console.log("♻️ Attempting reconnect...");
                connect(userJid, password, true).catch((err) => {
                  console.warn("⚠️ Auto-reconnect failed:", err.message);
                });
              }, 5000);
            }
            break;

          case Strophe.Status.CONNFAIL:
            console.log("❗ Connection failed");
            setConnected(false);
            if (!isReconnect) {
              reject(new Error("XMPP connection failed"));
            }
            break;

          default:
            console.log("ℹ️ Status:", status);
        }
      });
    });
  };

  // 🚪 Safe Disconnect
  const disconnect = () => {
    const conn = connectionRef.current;
    if (!conn) return;

    try {
      if (conn.connected && conn.socket) {
        conn.send($pres({ type: "unavailable" }).tree());
        conn.flush();

        // ⏳ Wait briefly before disconnecting
        setTimeout(() => {
          conn.disconnect();
        }, 300); // 300–500ms is enough
      } else {
        conn.disconnect();
      }
    } catch (err) {
      console.warn("⚠️ Safe disconnect error:", err);
    } finally {
      setConnected(false);
      setJid(null);
      setOnlineUsers(new Set());
      connectionRef.current = null;
    }
  };

  // 🆕 Fetch roster list
  const getListContact = (conn) => {
    const iq = $iq({ type: "get" }).c("query", { xmlns: "jabber:iq:roster" });
    conn.sendIQ(iq, (result) => {
      const items = result.getElementsByTagName("item");
      const list = Array.from(items).map((item) => ({
        jid: item.getAttribute("jid"),
        name: item.getAttribute("name") || item.getAttribute("jid"),
        subscription: item.getAttribute("subscription"),
      }));
      setRoster(list);

      // send presence to all roster contacts
      list.forEach((contact) => {
        conn.send($pres({ to: contact.jid, type: "subscribe" }).tree());
      });
    });
  };

  // 🆕 Add contact to roster
  const addRosterContact = (jid, name) => {
    if (!connectionRef.current || !connected) return;

    const conn = connectionRef.current;

    // 1️⃣ Send roster set IQ
    const iq = $iq({ type: "set" })
      .c("query", { xmlns: "jabber:iq:roster" })
      .c("item", { jid, name });
    conn.sendIQ(
      iq,
      () => {
        // 2️⃣ Send presence subscription request
        const pres = $pres({ to: jid, type: "subscribe" });
        conn.send(pres.tree());

        // 3️⃣ Update local roster state
        setRoster((prev) => [...prev, { jid, name, subscription: "none" }]);
      },
      (err) => {
        console.error("❌ Failed to add roster contact:", err);
      }
    );
  };

  // 🆕 Check user existence
  function checkUserExistence(jid, callback) {
    if (!connectionRef.current || !connected) return;

    const conn = connectionRef.current;
    const iq = $iq({ type: "get", to: jid, id: "ping1" }).c("ping", {
      xmlns: "urn:xmpp:ping",
    });

    conn.sendIQ(
      iq,
      () => {
        callback(true);
      },
      (err) => {
        const condition = err.getElementsByTagName("error")[0];
        if (condition) {
          const type = condition.getAttribute("type");
          const text = condition.textContent;
        }
        callback(false);
      }
    );
  }

  async function getLastSeen(jid, callback) {
    const conn = connectionRef.current;

    if (!conn || !connected) {
      callback(null);
      return;
    }

    const iq = $iq({ type: "get", to: jid }).c("query", {
      xmlns: "jabber:iq:last",
    });

    conn.sendIQ(
      iq,
      (stanza) => {
        const query = stanza.getElementsByTagName("query")[0];
        if (!query) {
          callback(null);
          return null;
        }
        const seconds = Number(query.getAttribute("seconds") || 0);
        callback({ jid, seconds });
        return null;
      },
      (err) => {
        callback(null);

        return null;
      }
    );
  }

  // 💬 Send message
  const sendMessage = (id, from, to, conversation) => {
    if (!connectionRef.current || !connected) return;
    const message = $msg({
      to: to.phoneNumber + "@chat.cuaps.id",
      type: "chat",
      id: id,
    })
      .c("sender")
      .t(`<![CDATA[${JSON.stringify(from)}]]>`)
      .up()
      .c("conversation")
      .t(`<![CDATA[${conversation}]]>`)
      .up()
      .c("request", { xmlns: "urn:xmpp:receipts" });

    connectionRef.current.send(message.tree());
  };
  // send read message
  function markMessagesAsRead(connection, messages) {
    const messagesReadSuccess = [];
    messages.forEach(({ partnerJid, unreadIds }) => {
      const fromValid = Number(partnerJid.split("@")[0]);
      const fromJid = Array.from(onlineUsers)?.find(
        (u) => Number(u.split("@")[0]) == fromValid
      );
      if (location.pathname.includes(fromValid)) {
        unreadIds.forEach((id) => {
          const displayed = $msg({ to: fromJid.split("/")[0], type: "chat" }).c(
            "displayed",
            {
              xmlns: "urn:xmpp:chat-markers:0",
              id: id.toString(),
            }
          );
          connection.send(displayed);
          messagesReadSuccess.push(fromValid);
        });
      }
    });
    setUnreadModule((prev) => ({
      ...prev,
      message: prev?.message?.filter(
        (item) => !messagesReadSuccess.includes(Number(item.partnerJid))
      ),
    }));
  }

  // 🧠 Handle presence & message
  useEffect(() => {
    const conn = connectionRef.current;
    if (!conn) return;

    conn.deleteHandler?.();

    // 🆕 Presence handler
    const onPresence = (presence) => {
      const from = presence.getAttribute("from");
      const type = presence.getAttribute("type") || "available";
      const bareFrom = Strophe.getBareJidFromJid(from).split("@")[0];
      setOnlineUsers((prev) => {
        const updated = new Set(prev);
        const bareFrom = from.split("@")[0]; // ambil sebelum '@'

        // cari apakah sudah ada user dengan bareFrom
        let existingUser = null;
        for (const user of updated) {
          if (user.split("@")[0] === bareFrom) {
            existingUser = user;
            break;
          }
        }

        if (type === "unavailable") {
          if (existingUser) updated.delete(existingUser);
        } else if (type === "available") {
          // kalau sudah ada user dengan nama sama, hapus dulu biar gak duplikat
          if (existingUser) updated.delete(existingUser);
          updated.add(from); // tambahkan versi terbaru (device/resource baru)
        }

        if (type === "subscribe") {
          // auto-accept dan request mutual presence
          conn.send($pres({ to: from, type: "subscribed" }).tree());
          conn.send($pres({ to: from, type: "subscribe" }).tree());
        }

        return updated;
      });

      return true;
    };

    conn.addHandler(onPresence, null, "presence", null, null, null);

    // 🧠 Message  handler
    const onMessage = (msg) => {
      const from = msg.getAttribute("from");
      const to = msg.getAttribute("to");
      const id = msg.getAttribute("id");
      const conversationEl = msg.getElementsByTagName("conversation")[0];
      const senderEl = msg.getElementsByTagName("sender")[0];

      const requestEl = msg.getElementsByTagName("request")[0];
      const receivedEl = msg.getElementsByTagName("received")[0];
      const displayedEl = msg.getElementsByTagName("displayed")[0];
      const fromBare = Strophe.getBareJidFromJid(from);
      const toBare = Strophe.getBareJidFromJid(to);
      const myBareJid = Strophe.getBareJidFromJid(jid);

      if (conversationEl) {
        let rawConversation = Strophe.getText(conversationEl)
          .replace(/&quot;/g, '"')
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&amp;/g, "&")
          .replace(/^<!\[CDATA\[|\]\]>$/g, "")
          .trim();

        let rawSender = Strophe.getText(senderEl)
          .replace(/&quot;/g, '"')
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&amp;/g, "&")
          .replace(/^<!\[CDATA\[|\]\]>$/g, "")
          .trim();

        const conversation = JSON.parse(rawConversation);
        const sender = JSON.parse(rawSender);
        // ✅ kamu adalah penerima
        const isIncoming =
          toBare === myBareJid &&
          fromBare !== myBareJid &&
          sender.phoneNumber !== myBareJid.split("@")[0];

        // ✅ kamu adalah pengirim
        const isOutgoing = fromBare === myBareJid;

        if (isIncoming) {
          setNewMessage(conversation);
          addAudioAlert();
          if (requestEl) {
            const currentPath = window.location.pathname;
            const fromValid = from.split("@")[0];
            // ✅ kirim delivery receipt
            const receipt = $msg({ to: from, type: "chat" }).c("received", {
              xmlns: "urn:xmpp:receipts",
              id,
            });
            conn.send(receipt.tree());

            // ✅ kirim delivery read
            if (currentPath.includes(fromValid)) {
              const displayed = $msg({ to: from, type: "chat" }).c(
                "displayed",
                { xmlns: "urn:xmpp:chat-markers:0", id }
              );
              conn.send(displayed);
            } else {
              setUnreadModule((prev) => ({
                ...prev,
                message: prev.message.find(
                  (item) =>
                    Number(item.partnerJid) == Number(sender.phoneNumber)
                )
                  ? [
                      ...prev.message.map((item) =>
                        Number(item.partnerJid) == Number(sender.phoneNumber)
                          ? {
                              ...item,
                              unreadIds: [...item.unreadIds, id],
                            }
                          : item
                      ),
                    ]
                  : [
                      {
                        partnerJid: sender.phoneNumber,
                        unreadIds: [id],
                      },
                    ],
              }));
            }
          }
        } else if (isOutgoing) {
          // tampilkan di chatbox sebagai pesan dari diri sendiri
        }
      }

      // 🧠 Always return true to keep this handler active!
      return true;
    };

    conn.addHandler(onMessage, null, "message", null, null, null);

    // 🧠 Receipt handler
    function onReceipt(msg) {
      const received = msg.getElementsByTagName("received")[0];
      const displayed = msg.getElementsByTagName("displayed")[0];

      if (received) {
        const id = received.getAttribute("id");

        // console.log(`✅ Message ${id} was received`);
      }
      if (displayed) {
        const id = displayed.getAttribute("id");

        // console.log(`✅ Message ${id} was read`);
        setNewMessageRead(id);
      }
      return true;
    }
    conn.addHandler(onReceipt, null, "message");

    return () => {
      conn.deleteHandler(onMessage);
      conn.deleteHandler(onReceipt);
      conn.deleteHandler(onPresence);
    };
  }, [connected, jid]);

  useEffect(() => {
    if (unreadModule?.message?.length == 0) return;
    const conn = connectionRef.current;
    if (!conn) return;
    markMessagesAsRead(conn, unreadModule?.message);
  }, [location.pathname]);

  return (
    <XmppContext.Provider
      value={{
        connected,
        jid,
        connect,
        sendMessage,
        disconnect,
        roster, // 🆕 expose roster
        onlineUsers: Array.from(onlineUsers),
        reconnectTimeoutRef,
        addRosterContact,
        checkUserExistence,
        getLastSeen,
      }}
    >
      {children}
    </XmppContext.Provider>
  );
}
